import { useNavigate } from "react-router-dom";
import { Building2, Heart, ArrowRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";

/**
 * Modal that asks the visitor whether they're ordering for a private event
 * or as an agency / institution, and routes them to the correct portal.
 *
 * Props:
 *   open: boolean
 *   onOpenChange: (v: boolean) => void
 */
export const MenuPortalChooserModal = ({ open, onOpenChange }) => {
  const navigate = useNavigate();

  const go = (path) => {
    onOpenChange(false);
    navigate(path);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="menu-portal-chooser-modal"
        className="sm:max-w-[480px] p-0 overflow-hidden rounded-2xl border-none shadow-[0_30px_80px_-30px_rgba(17,17,17,0.45)]"
      >
        {/* Header strip */}
        <div className="bg-brand-charcoal text-white px-7 pt-8 pb-7">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-gold">
            Browse the Menu
          </p>
          <DialogHeader className="mt-2 space-y-2">
            <DialogTitle className="font-display font-black text-[24px] sm:text-[26px] tracking-tight leading-[1.1] text-white text-left">
              Who&rsquo;s the order for?
            </DialogTitle>
            <DialogDescription className="text-[13.5px] text-white/70 leading-relaxed text-left">
              We&rsquo;ll route you to the right portal — pricing and checkout
              differ for private events vs. institutional accounts.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-5 sm:px-6 py-6 bg-white grid gap-3">
          <button
            type="button"
            data-testid="portal-chooser-private-btn"
            onClick={() => go("/menu")}
            className="group flex items-start gap-4 rounded-2xl border border-black/10 bg-white p-4 sm:p-5 text-left hover:border-brand-teal hover:bg-brand-cream/60 transition-all"
          >
            <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-teal/12 text-brand-teal group-hover:bg-brand-teal group-hover:text-white transition-colors">
              <Heart className="h-5 w-5" />
            </span>
            <span className="flex-1">
              <span className="block text-[14.5px] font-bold text-brand-charcoal leading-tight">
                Private Catering
              </span>
              <span className="mt-0.5 block text-[12.5px] text-brand-charcoal/65 leading-snug">
                Birthdays, office meals, weddings, personal events. Retail pricing, pay by card.
              </span>
            </span>
            <ArrowRight className="h-4 w-4 mt-1 text-brand-charcoal/40 group-hover:text-brand-teal group-hover:translate-x-0.5 transition-all" />
          </button>

          <button
            type="button"
            data-testid="portal-chooser-agency-btn"
            onClick={() => go("/agency")}
            className="group flex items-start gap-4 rounded-2xl border border-black/10 bg-white p-4 sm:p-5 text-left hover:border-brand-gold hover:bg-brand-cream/60 transition-all"
          >
            <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-gold/15 text-brand-gold-dark group-hover:bg-brand-gold group-hover:text-brand-charcoal transition-colors">
              <Building2 className="h-5 w-5" />
            </span>
            <span className="flex-1">
              <span className="block text-[14.5px] font-bold text-brand-charcoal leading-tight">
                Agency / Institutional
              </span>
              <span className="mt-0.5 block text-[12.5px] text-brand-charcoal/65 leading-snug">
                NYC agencies, schools, nonprofits, corporates. Institutional pricing, PO-based checkout.
              </span>
            </span>
            <ArrowRight className="h-4 w-4 mt-1 text-brand-charcoal/40 group-hover:text-brand-gold-dark group-hover:translate-x-0.5 transition-all" />
          </button>

          <p
            data-testid="portal-chooser-helper"
            className="mt-2 text-center text-[11.5px] text-brand-charcoal/55 leading-snug"
          >
            Not sure?{" "}
            <a
              href="/who-we-serve"
              className="font-semibold text-brand-teal hover:underline"
            >
              See who we serve →
            </a>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MenuPortalChooserModal;
